/**
 * Guardia "modifica dati fiscali" — tipi e parsing condivisi (R6-B).
 *
 * Il backend risponde 409 con un detail STRUTTURATO quando il payload cambia
 * un campo che alimenta il CessionarioCommittente della FatturaPA e il cliente
 * ha fatture emesse o pagate. Il frontend elenca le fatture coinvolte e, su
 * conferma, rinvia la stessa PATCH con `conferma_modifica_fiscale: true`.
 *
 * Usato da entrambe le schermate che scrivono l'anagrafica cliente
 * (DettaglioClientePage → PATCH /customers, DettaglioRichiestaPage → PATCH
 * /richieste), così le due non possono divergere nel comportamento.
 */

/** Riferimento sintetico a una fattura, come lo espone il backend. */
export interface FatturaRiferimento {
  id: string;
  numero: string | null;
  data: string | null;
  stato: string;
}

/** Body del 409 di conferma modifica fiscale. */
export interface ConfermaModificaFiscaleDetail {
  code: 'conferma_modifica_fiscale_richiesta';
  message: string;
  campi: string[];
  fatture: FatturaRiferimento[];
}

/**
 * Riconosce il 409 di conferma modifica fiscale e ne estrae il detail.
 *
 * Ritorna null per qualunque altro errore, incluso l'altro 409 dell'endpoint
 * (P.IVA duplicata), che ha un detail testuale. La discriminante è il campo
 * `code`: non va usato il testo del messaggio.
 */
export function parseConfermaFiscale(err: unknown): ConfermaModificaFiscaleDetail | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Partial<ConfermaModificaFiscaleDetail>;
  if (d.code !== 'conferma_modifica_fiscale_richiesta') return null;
  return {
    code: d.code,
    message: d.message ?? '',
    campi: Array.isArray(d.campi) ? d.campi : [],
    fatture: Array.isArray(d.fatture) ? d.fatture : [],
  };
}

/**
 * Detail testuale di un errore, quando c'è. I 409 strutturati hanno un detail
 * oggetto: `undefined` evita il `.includes is not a function` che si otterrebbe
 * trattandolo come stringa.
 */
export function detailTesto(err: unknown): string | undefined {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : undefined;
}
